// Records existing call streams only. Never requests another camera/microphone.
export function recordingSupported(video = true) {
  return typeof MediaRecorder !== 'undefined'
    && !!(window.AudioContext || window.webkitAudioContext)
    && (!video || typeof HTMLCanvasElement.prototype.captureStream === 'function');
}

export function createRecordingAudioContext() {
  const Audio = window.AudioContext || window.webkitAudioContext;
  return new Audio();
}

export function createCallRecorder({ localStream, remoteStream, video, onComplete, onError, audioContext }) {
  const Audio = window.AudioContext || window.webkitAudioContext;
  const context = audioContext || new Audio();
  const destination = context.createMediaStreamDestination();
  const sources = [];
  const previews = [];
  let timer, canvasStream, recorder, stopped = false;
  const chunks = [];
  const release = () => {
    clearInterval(timer);
    previews.forEach(el => { el.pause(); el.srcObject = null; });
    sources.forEach(source => source.disconnect());
    canvasStream?.getTracks().forEach(track => track.stop());
    destination.stream.getTracks().forEach(track => track.stop());
    context.close().catch(() => {});
  };
  try {
    [localStream, remoteStream].forEach(stream => {
      const tracks = stream?.getAudioTracks() || [];
      if (tracks.length) {
        const source = context.createMediaStreamSource(new MediaStream(tracks));
        source.connect(destination); sources.push(source);
      }
    });
    const output = new MediaStream(destination.stream.getAudioTracks());
    if (video) {
      const canvas = document.createElement('canvas');
      canvas.width = 1280; canvas.height = 720;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Video recording is unavailable in this browser.');
      [remoteStream, localStream].forEach(stream => {
        const el = document.createElement('video');
        el.muted = true; el.playsInline = true; el.srcObject = stream;
        el.play().catch(() => {}); previews.push(el);
      });
      const draw = () => {
        ctx.fillStyle = '#080d1c'; ctx.fillRect(0, 0, 1280, 720);
        previews.forEach((el, i) => {
          const left = i * 640;
          if (el.readyState >= 2 && el.videoWidth && el.videoHeight) {
            const scale = Math.min(624 / el.videoWidth, 640 / el.videoHeight);
            const w = el.videoWidth * scale, h = el.videoHeight * scale;
            ctx.drawImage(el, left + (640-w)/2, 24 + (640-h)/2, w, h);
          }
          ctx.fillStyle = '#fff'; ctx.font = '20px sans-serif';
          ctx.fillText(i ? 'You' : 'Participant', left + 20, 700);
        });
      };
      draw(); timer = setInterval(draw, 1000 / 15);
      canvasStream = canvas.captureStream(15);
      canvasStream.getVideoTracks().forEach(track => output.addTrack(track));
    }
    const types = video
      ? ['video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
      : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
    const mimeType = types.find(type => MediaRecorder.isTypeSupported(type));
    if (!mimeType) throw new Error('Recording format is not supported in this browser.');
    recorder = new MediaRecorder(output, { mimeType });
    recorder.ondataavailable = event => { if (event.data?.size) chunks.push(event.data); };
    recorder.onerror = () => { onError('Recording failed. Any captured portion will be offered for download.'); stop(); };
    recorder.onstop = () => {
      release();
      if (chunks.length) onComplete(new Blob(chunks, { type: recorder.mimeType || mimeType }));
      else onError('No recording was captured.');
    };
    // Construct/resume inside the consent click/message path; report failure
    // rather than silently producing a recording with missing mixed audio.
    context.resume().catch(() => { onError('Recording audio could not start.'); stop(); });
    recorder.start(1000);
  } catch (error) { release(); throw error; }
  function stop() {
    if (stopped) return;
    stopped = true;
    if (recorder?.state !== 'inactive') recorder.stop();
    else release();
  }
  return { stop };
}
